import { Injectable, ComponentRef } from '@angular/core';
import { MotiChatDialogComponent } from '../components/moti-chat/moti-chat.component';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TopicService } from './topic.service';
import { ConvoService } from './convo.service';
import { ObjectType } from '../../shared/models/object.model';
import { switchMap, catchError, of, Observable, map } from 'rxjs';
import { Convo } from '../models/convo';

@Injectable({
  providedIn: 'root',
})
export class MotiChatService {
  private static readonly OVERLAY_CONFIG: OverlayConfig = {
    width: '60vw',
    height: '85vh',
    maxWidth: '1200px',
    maxHeight: '100vh',
    panelClass: 'moti-chat-dialog',
    hasBackdrop: true,
    positionStrategy: null, // Will be set dynamically
    scrollStrategy: null // Will be set dynamically
  };

  private activeChats: Map<string, OverlayRef> = new Map();
  private chatStartTimes: Map<string, number> = new Map();

  constructor(
    private overlay: Overlay,
    private topicService: TopicService,
    private convoService: ConvoService,
  ) {}

  /**
   * Opens a chat for a specific object
   * @param objectId - Unique identifier for the object
   * @param objectName - Display name for the object header
   * @param objectType - Type of the object (hub, device, tile, app, user)
   */
  openChat(objectId: string, objectName: string, objectType: ObjectType = ObjectType.USER): void {
    this.closeChat(objectId);

    // Store chat start time for duration tracking
    this.chatStartTimes.set(objectId, Date.now());

    const convo$ = this.initializeChatSession(objectId, objectName, objectType).pipe(
      catchError((error) => {
        console.error('Failed to initialize chat session:', error);
        return of(null);
      })
    );

    this.createChatDialog(objectId, objectName, objectType, convo$);
  }

  private createChatDialog(objectId: string, objectName: string, objectType: string, convo$: Observable<{ topicId: string, convo: Convo } | null>): void {
    const overlayRef = this.createOverlay();
    const componentRef = this.attachComponent(overlayRef);
    
    this.configureComponent(componentRef, objectName, objectId, objectType, convo$);
    this.setupEventHandlers(overlayRef, componentRef, objectId);
    
    this.activeChats.set(objectId, overlayRef);
  }

  private createOverlay() {
    const config = {
      ...MotiChatService.OVERLAY_CONFIG,
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    };
    return this.overlay.create(config);
  }

  private attachComponent(overlayRef: OverlayRef) {
    const portal = new ComponentPortal(MotiChatDialogComponent);
    return overlayRef.attach(portal);
  }

  private configureComponent(
    componentRef: ComponentRef<MotiChatDialogComponent>, 
    objectName: string, 
    objectId: string, 
    objectType: string, 
    convo$: Observable<{ topicId: string, convo: Convo } | null>
  ): void {
    const instance = componentRef.instance;
    instance.objectName = objectName;
    instance.objectId = objectId;
    instance.objectType = objectType;
    instance.convo$ = convo$.pipe(map(result => result?.convo || null));
  }

  private setupEventHandlers(overlayRef: OverlayRef, componentRef: ComponentRef<MotiChatDialogComponent>, objectId: string): void {
    componentRef.instance.closeRequested.subscribe(() => this.closeChat(objectId));
    overlayRef.backdropClick().subscribe(() => this.closeChat(objectId));
  }

  /**
   * Closes the chat for a specific object
   * @param objectId - Unique identifier for the object
   */
  closeChat(objectId: string): void {
    const overlayRef = this.activeChats.get(objectId);
    if (overlayRef) {
      this.chatStartTimes.delete(objectId);

      overlayRef.dispose();
      this.activeChats.delete(objectId);
    }
  }

  /**
   * Initializes or retrieves existing topic and convo for an object
   */
  private initializeChatSession(objectId: string, objectName: string, objectType: ObjectType): Observable<{ topicId: string, convo: Convo }> {
    // Convert ObjectType enum to lowercase string for API
    const objectTypeStr = objectType.toLowerCase();

    return this.topicService.findTopicForObject$(objectId, objectTypeStr).pipe(
      switchMap(existingTopic => {
        if (existingTopic) {
          return this.getOrCreateConvoForTopic(existingTopic.id, objectName);
        } else {
          return this.topicService.createTopicForObject$(objectId, objectTypeStr).pipe(
            switchMap(createdTopic => this.createConvoForTopic(createdTopic.id, objectName))
          );
        }
      })
    );
  }

  private getOrCreateConvoForTopic(topicId: string, objectName: string): Observable<{ topicId: string, convo: Convo }> {
    return this.convoService.findConvoForTopic$(topicId).pipe(
      switchMap(existingConvo => {
        if (existingConvo) {
          return of({ topicId, convo: existingConvo });
        } else {
          return this.createConvoForTopic(topicId, objectName);
        }
      })
    );
  }

  private createConvoForTopic(topicId: string, objectName: string): Observable<{ topicId: string, convo: Convo }> {
    const convoName = `Chat with ${objectName}`;
    return this.convoService.createConvoForTopic$(topicId, convoName).pipe(
      map(convo => ({ topicId, convo }))
    );
  }


}
